import { useSeoMeta, useHead } from '@unhead/react'
import { useState } from 'react'
import { ExportButtons } from '@/components/ExportButtons'
import { ExportData } from '@/utils/exportUtils'

function BlockWallCalculator() {
  useSeoMeta({
    title: 'Concrete Block Calculator | Cinder Block Estimator Tool',
    description: 'Calculate blocks needed for your wall project. Free concrete block calculator and cinder block estimator. Get instant material estimates for any wall size.',
    ogTitle: 'Concrete Block Calculator | Cinder Block Estimator',
    ogDescription: 'Calculate concrete blocks and cinder blocks needed for walls.',
    ogUrl: 'https://www.concrete-calculator.com/block-wall-calculator/',
    ogType: 'website',
    twitterCard: 'summary_large_image'
  })

  useHead({
    link: [
      { rel: 'canonical', href: 'https://www.concrete-calculator.com/block-wall-calculator/' }
    ]
  })

  const [wallLength, setWallLength] = useState('')
  const [wallHeight, setWallHeight] = useState('')
  const [blockSize, setBlockSize] = useState('standard') // standard (8x8x16) or large (8x8x12)
  const [wasteFactor, setWasteFactor] = useState('5')
  const [results, setResults] = useState<{
    blocksNeeded: number
    blocksWithWaste: number
    bagsMortar: number
    squareFeet: number
  } | null>(null)

  const calculate = () => {
    const length = parseFloat(wallLength)
    const height = parseFloat(wallHeight)
    const waste = parseFloat(wasteFactor) / 100

    if (isNaN(length) || isNaN(height) || length <= 0 || height <= 0) {
      return
    }

    const squareFeet = length * height

    // Standard 8x8x16 block = 1.125 sq ft per block (including mortar joints)
    // Large 8x8x12 block = 0.89 sq ft per block
    const sqFtPerBlock = blockSize === 'standard' ? 1.125 : 0.89
    const blocksBase = squareFeet / sqFtPerBlock
    const blocksWithWaste = Math.ceil(blocksBase * (1 + waste))

    // Mortar: approximately 3 bags of 80lb mortar per 100 blocks
    const mortarBags = Math.ceil((blocksWithWaste / 100) * 3)

    setResults({
      blocksNeeded: Math.ceil(blocksBase),
      blocksWithWaste: blocksWithWaste,
      bagsMortar: mortarBags,
      squareFeet: squareFeet
    })
  }

  const getExportData = (): ExportData => ({
    title: 'Block Wall Calculator Results',
    inputs: [
      { label: 'Wall Length', value: `${wallLength} feet` },
      { label: 'Wall Height', value: `${wallHeight} feet` },
      { label: 'Block Size', value: blockSize === 'standard' ? 'Standard (8"×8"×16")' : 'Large (8"×8"×12")' },
      { label: 'Waste Factor', value: `${wasteFactor}%` }
    ],
    results: [
      { label: 'Wall Area', value: `${results?.squareFeet.toFixed(2)} sq ft` },
      { label: 'Blocks Needed (Base)', value: `${results?.blocksNeeded} blocks` },
      { label: 'Blocks with Waste', value: `${results?.blocksWithWaste} blocks` },
      { label: 'Mortar Bags (80lb)', value: `${results?.bagsMortar} bags` }
    ]
  })

  const reset = () => {
    setWallLength('')
    setWallHeight('')
    setBlockSize('standard')
    setWasteFactor('5')
    setResults(null)
  }

  return (
    <div className="min-h-screen bg-white">
      <main className="max-w-6xl mx-auto px-4 py-8">
        <h1 style={{ fontSize: '2rem', color: '#4A4A4A', marginBottom: '1rem' }}>
          Concrete Block Calculator & Cinder Block Estimator
        </h1>
        
        <nav aria-label="Calculator Tools" className="mb-8">
          <a href="/" className="text-[#FF8C00] hover:underline">← Back to Home</a>
        </nav>

        {/* Calculator */}
        <div className="bg-white border-2 border-gray-200 rounded-lg p-6 md:p-8 mb-12 shadow-sm">
          <h2 className="text-xl font-bold text-[#4A4A4A] mb-6">Calculate Blocks Needed for Wall</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <label htmlFor="wallLength" className="block text-sm font-semibold text-[#4A4A4A] mb-2">
                Wall Length (feet)
              </label>
              <input
                id="wallLength"
                type="number"
                value={wallLength}
                onChange={(e) => setWallLength(e.target.value)}
                placeholder="Enter wall length"
                className="w-full px-4 py-3 text-lg border-2 border-gray-300 rounded-md focus:border-[#FF8C00] focus:outline-none"
                min="0"
                step="0.1"
              />
            </div>

            <div>
              <label htmlFor="wallHeight" className="block text-sm font-semibold text-[#4A4A4A] mb-2">
                Wall Height (feet)
              </label>
              <input
                id="wallHeight"
                type="number"
                value={wallHeight}
                onChange={(e) => setWallHeight(e.target.value)}
                placeholder="Enter wall height"
                className="w-full px-4 py-3 text-lg border-2 border-gray-300 rounded-md focus:border-[#FF8C00] focus:outline-none"
                min="0"
                step="0.1"
              />
            </div>

            <div>
              <label htmlFor="blockSize" className="block text-sm font-semibold text-[#4A4A4A] mb-2">
                Block Size
              </label>
              <select
                id="blockSize"
                value={blockSize}
                onChange={(e) => setBlockSize(e.target.value)}
                className="w-full px-4 py-3 text-lg border-2 border-gray-300 rounded-md focus:border-[#FF8C00] focus:outline-none"
              >
                <option value="standard">Standard (8"×8"×16")</option>
                <option value="large">Large (8"×8"×12")</option>
              </select>
            </div>

            <div>
              <label htmlFor="waste" className="block text-sm font-semibold text-[#4A4A4A] mb-2">
                Waste Factor (%)
              </label>
              <input
                id="waste"
                type="number"
                value={wasteFactor}
                onChange={(e) => setWasteFactor(e.target.value)}
                placeholder="5"
                className="w-full px-4 py-3 text-lg border-2 border-gray-300 rounded-md focus:border-[#FF8C00] focus:outline-none"
                min="0"
                max="20"
                step="1"
              />
            </div>
          </div>

          <div className="flex gap-3 mb-6">
            <button
              onClick={calculate}
              className="flex-1 bg-[#FF8C00] text-white font-bold py-3 px-6 rounded-md hover:bg-[#E67E00] transition-colors text-lg"
            >
              Calculate
            </button>
            <button
              onClick={reset}
              className="flex-1 bg-gray-500 text-white font-bold py-3 px-6 rounded-md hover:bg-gray-600 transition-colors text-lg"
            >
              Reset
            </button>
          </div>

          {results && (
            <div className="bg-gray-50 border-2 border-[#FF8C00] rounded-lg p-6">
              <div className="flex justify-between items-center mb-4 flex-wrap gap-3">
                <h3 className="text-xl font-bold text-[#4A4A4A]">Results</h3>
                <ExportButtons data={getExportData()} />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="bg-white p-4 rounded border border-gray-200">
                  <p className="text-sm text-gray-600 mb-1">Wall Area</p>
                  <p className="text-2xl font-bold text-[#4A4A4A]">{results.squareFeet.toFixed(2)} sq ft</p>
                </div>
                <div className="bg-white p-4 rounded border border-gray-200">
                  <p className="text-sm text-gray-600 mb-1">Blocks Needed (Base)</p>
                  <p className="text-2xl font-bold text-[#4A4A4A]">{results.blocksNeeded} blocks</p>
                </div>
                <div className="bg-white p-4 rounded border border-gray-200">
                  <p className="text-sm text-gray-600 mb-1">Blocks with Waste</p>
                  <p className="text-2xl font-bold text-[#FF8C00]">{results.blocksWithWaste} blocks</p>
                </div>
                <div className="bg-white p-4 rounded border border-gray-200">
                  <p className="text-sm text-gray-600 mb-1">Mortar Bags (80lb)</p>
                  <p className="text-2xl font-bold text-[#FF8C00]">{results.bagsMortar} bags</p>
                </div>
              </div>
            </div>
          )}

          <div className="mt-6 text-sm text-gray-600">
            <p className="mb-2">
              <strong>How to use:</strong> Enter your wall dimensions in feet. Standard blocks (8"×8"×16") cover approximately 1.125 sq ft including mortar joints.
            </p>
            <p>
              <strong>Note:</strong> Add 5-10% waste factor for cuts and breakage.
            </p>
          </div>
        </div>

        {/* FAQ Section */}
        <section aria-labelledby="faq-heading" className="mt-12">
          <h2 id="faq-heading" className="text-2xl font-bold text-[#4A4A4A] mb-4">
            Frequently Asked Questions
          </h2>
          <div className="space-y-6">
            <div>
              <h3 className="font-semibold text-lg text-[#4A4A4A] mb-2">
                How many blocks per square foot?
              </h3>
              <p className="text-gray-700">
                Standard 8"×8"×16" concrete blocks cover approximately 1.125 square feet per block when accounting for mortar joints. This means you need about 0.89 blocks per square foot.
              </p>
            </div>
            <div>
              <h3 className="font-semibold text-lg text-[#4A4A4A] mb-2">
                What is the difference between concrete block and cinder block?
              </h3>
              <p className="text-gray-700">
                Concrete blocks are made with portland cement and aggregates. Cinder blocks (older style) use coal cinders. Modern construction uses concrete blocks which are stronger and more durable.
              </p>
            </div>
          </div>
        </section>
      </main>
    </div>
  )
}

export default BlockWallCalculator
